/**
 * RBAC roles used across backend and frontend.
 *
 * - CO         — superuser; full financial + AI access, admin over the whole
 *                command platform (every task board, every officer).
 * - DATA_ENTRY — accounts clerk; transaction entry only. No tasks, no inbox.
 * - OFFICER    — battalion officer; task management + messaging only. Walled
 *                off from the financial/AI surface. Self-registers and is
 *                approved by the CO before first login.
 *
 * The Prisma `Role` enum mirrors these literals.
 *
 * NOTE: superuser access is NOT role alone — the officer holding the 2IC
 * appointment also wields the CO's authority. Always gate CO-level capability
 * through `hasCoPowers()` below, never an inline `role === 'CO'`.
 */

export const Role = {
  CO: 'CO',
  DATA_ENTRY: 'DATA_ENTRY',
  OFFICER: 'OFFICER',
} as const;

export type Role = (typeof Role)[keyof typeof Role];

export const ALL_ROLES: readonly Role[] = [Role.CO, Role.DATA_ENTRY, Role.OFFICER] as const;

/**
 * The appointment that confers superuser (CO-equivalent) authority. The 2IC
 * stands in for the Commanding Officer, so whoever currently holds the 2IC
 * slot gets the full command surface. Kept as a literal here (rather than
 * importing the appointments module) so this stays the single, dependency-free
 * source of truth for the privilege rule.
 */
export const SUPERUSER_APPOINTMENT = '2IC';

/** Minimal shape needed to decide CO-level authority. */
export interface CoPowerSubject {
  role: Role;
  appointment?: string | null;
}

/**
 * "Has the Commanding Officer's authority." True for the CO, and for the
 * officer who currently holds the 2IC appointment. This is the SINGLE source
 * of truth for every CO-level gate on both backend and frontend — route
 * guards, middleware, service-layer capability checks, and UI rendering.
 *
 * It deliberately covers CAPABILITY, not identity: a 2IC is still displayed as
 * an officer with their own rank and the 2IC chip — only what they're allowed
 * to DO is elevated. Display/identity branches keep their explicit
 * `role === 'CO'` check.
 */
export function hasCoPowers(subject: CoPowerSubject | null | undefined): boolean {
  if (!subject) return false;
  return subject.role === Role.CO || subject.appointment === SUPERUSER_APPOINTMENT;
}
